import { useEffect, useRef } from "react";

const STATE_KEY = "apollosOverlay";

interface Overlay {
  close: () => void;
  url: string;
  pushed: boolean;
}

const stack: Overlay[] = [];
let pendingPops = 0;
let listening = false;
// Where the app last took the URL. A pop that lands anywhere else has to reach
// the router, even when it lands on an entry we pushed.
let routerUrl = "";
let nativePush: History["pushState"];
let nativeReplace: History["replaceState"];

const depthOf = (state: unknown) => {
  const depth = (state as Record<string, unknown> | null)?.[STATE_KEY];
  return typeof depth === "number" ? depth : 0;
};

const push = (overlay: Overlay) => {
  window.history.pushState(
    { ...window.history.state, [STATE_KEY]: stack.indexOf(overlay) + 1 },
    ""
  );
  overlay.pushed = true;
};

// Web embeds run in an iframe, where a pushed entry lands in the church
// site's history.
const isTopWindow = () => window.top === window.self;

const goBack = (steps: number) => {
  pendingPops += 1;
  window.history.go(-steps);
};

// A URL the browser will not accept is not ours to reason about: read it as
// the page we are on so the call goes through untouched.
const hrefOf = (url: string | URL) => {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return window.location.href;
  }
};

const isOnTopOverlayEntry = () => {
  const top = stack[stack.length - 1];
  return (
    top?.pushed === true &&
    top.url === window.location.href &&
    depthOf(window.history.state) === stack.length
  );
};

// A URL change ends the life of every overlay entry: it stands for the page
// being left, as does a pop we still have in flight.
const leave = (url: string | URL) => {
  routerUrl = hrefOf(url);
  pendingPops = 0;
  stack.splice(0);
};

// The app changes the URL with pushState, which would bury the entry of an
// overlay that is open at the time: the next back would land on that entry
// instead of reaching the router. Hand the entry to the new page instead.
const watchNavigation = () => {
  nativePush = window.history.pushState.bind(window.history);
  nativeReplace = window.history.replaceState.bind(window.history);
  routerUrl = window.location.href;

  window.history.pushState = (state, unused, url) => {
    if (url == null || hrefOf(url) === window.location.href) {
      nativePush(state, unused, url);
      return;
    }
    const takeOverOverlayEntry = isOnTopOverlayEntry();
    leave(url);
    (takeOverOverlayEntry ? nativeReplace : nativePush)(state, unused, url);
  };

  window.history.replaceState = (state, unused, url) => {
    if (url != null && hrefOf(url) !== window.location.href) leave(url);
    nativeReplace(state, unused, url);
  };
};

const onPopState = (event: PopStateEvent) => {
  if (pendingPops > 0) {
    pendingPops -= 1;
    event.stopImmediatePropagation();
    if (pendingPops === 0) stack.filter((o) => !o.pushed).forEach(push);
    return;
  }
  const depth = depthOf(event.state);
  const pushed = stack.filter((o) => o.pushed).length;
  const top = stack[depth];
  if (top?.pushed && top.url === window.location.href) {
    event.stopImmediatePropagation();
    stack
      .splice(depth)
      .reverse()
      .forEach((overlay) => overlay.close());
  } else if (depth > pushed && window.location.href === routerUrl) {
    // Forward onto an entry whose overlay is already closed.
    event.stopImmediatePropagation();
    goBack(depth - pushed);
  } else {
    // An entry the router owns, or one a navigation left behind: either way it
    // moves the URL away from the page the router is rendering, so let it
    // through.
    routerUrl = window.location.href;
  }
};

const listen = () => {
  if (listening) return;
  watchNavigation();
  window.addEventListener("popstate", onPopState);
  listening = true;
};

// Call before React Router hydrates. Listeners on window run in the order they
// were added, and a same-URL pop that reaches the router re-runs every loader.
export function listenForOverlayBack() {
  if (!isTopWindow()) return;
  listen();
  // A reload keeps the entry of an overlay that was open.
  const depth = depthOf(window.history.state);
  if (depth > 0) goBack(depth);
}

const open = (close: () => void) => {
  listen();
  const overlay = { close, url: window.location.href, pushed: false };
  stack.push(overlay);
  if (pendingPops === 0) push(overlay);
  return overlay;
};

const release = (overlay: Overlay) => {
  const index = stack.indexOf(overlay);
  if (index === -1) return;
  const pushed = stack.splice(index).filter((o) => o.pushed).length;
  if (pushed > 0 && window.location.href === overlay.url) goBack(pushed);
};

export function useCloseOnBack(isOpen: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen || !isTopWindow()) return undefined;
    const overlay = open(() => onCloseRef.current());
    return () => release(overlay);
  }, [isOpen]);
}
